"use client";

import { ArrowDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Blueprint() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.3
  });
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: paragraphRef,
    isVisible: paragraphVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imagesRef,
    isVisible: imagesVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const stats = [{
    label: "Engine One Funding for first mortgage debt and gap equity",
    value: "Debt",
    caption: "First & Gap"
  }, {
    label: "Engine Two Partnership for joint ventures based on capital, credit, or experience",
    value: "JV",
    caption: "Partnership"
  }, {
    label: "Engine Three Referral for a network for brokers, lenders, and professionals",
    value: "Referral",
    caption: "Network"
  }];
  return <section data-section-id="4212" id="the-capital-blueprint-hero" className="relative w-full min-h-[85vh] bg-[hsl(222_47%_7%)] overflow-hidden pt-24 md:pt-[120px] pb-16 md:pb-24">
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          <div className="lg:col-span-6 flex flex-col">
            <div ref={labelRef} className={`flex items-center gap-3 mb-10 md:mb-14 transition-all duration-700 ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="h-px w-8 bg-[hsl(215_20%_65%)]" aria-hidden="true" />
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-[hsl(215_20%_65%)]">
                BeneWealth Financial / 2025
              </span>
            </div>

            <h1 ref={headlineRef} className={`font-default font-bold text-white leading-[0.95] tracking-tight text-[56px] sm:text-[72px] lg:text-[92px] transition-all duration-1000 ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              The Infrastructure Behind Every Deal.
            </h1>

            <p ref={paragraphRef} className={`font-default text-[19px] leading-[1.55] text-white/80 mt-10 md:mt-12 max-w-[540px] transition-all duration-700 delay-300 ${paragraphVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              BeneWealth Financial funds real estate investors, partners with capital providers, and rewards the professionals who refer them, one professional-grade engine, three ways in.
            </p>

            <div ref={statsRef} className={`flex items-stretch mt-10 md:mt-14 transition-all duration-700 delay-500 ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              {stats.map((stat, i) => <div key={i} data-index={i} className="flex items-center">
                  <div className="flex flex-col pr-6 sm:pr-8">
                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[hsl(215_20%_65%)] mb-2">
                      {stat.label}
                    </span>
                    <div className="flex items-baseline gap-2">
                      <span className="font-default text-2xl md:text-3xl text-white">
                        {stat.value}
                      </span>
                      <span className="font-default text-xs md:text-sm text-white/60 italic">
                        {stat.caption}
                      </span>
                    </div>
                  </div>
                  {i < stats.length - 1 && <Separator orientation="vertical" className="bg-white/20 h-12 mr-6 sm:mr-8" />}
                </div>)}
            </div>

            <div ref={ctaRef} className={`mt-12 md:mt-16 transition-all duration-700 delay-700 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Button asChild size="lg" className="h-14 px-8 text-base font-default font-bold tracking-wide shadow-md hover:shadow-lg transition-shadow" style={{
              backgroundColor: "hsl(45 100% 50%)",
              color: "hsl(222 47% 7%)"
            }}>
                <Link to="/PreQualificationFormPage" className="flex items-center justify-center">
                  Get Pre-Qualified
                  <ArrowDown className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>

          <div ref={imagesRef} className="lg:col-span-6 relative min-h-[480px] sm:min-h-[560px] lg:min-h-[680px] mt-8 lg:mt-0">
            <div className={`absolute left-0 lg:left-[-8%] bottom-0 lg:bottom-8 w-[55%] sm:w-[48%] lg:w-[44%] aspect-[4/5] rounded-none overflow-hidden shadow-lg transition-all duration-1000 delay-200 ${imagesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/real_estate_blueprint_vault.png" alt="Architectural blueprint of a real estate development project" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-multiply" style={{
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n2'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n2)'/%3E%3C/svg%3E\")"
            }} aria-hidden="true" />
            </div>

            <div className={`absolute z-20 left-[38%] lg:left-[32%] top-[52%] -translate-y-1/2 transition-all duration-1000 delay-500 ${imagesVisible ? "opacity-100 scale-100" : "opacity-0 scale-75"}`} aria-hidden="true">
              <svg width="56" height="56" viewBox="0 0 64 64" fill="none" style={{
              color: "hsl(45 100% 50%)"
            }}>
                <path d="M44 8L20 56" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M44 8c2 4 4 6 7 7M44 8c-1 4-1 7 1 10M44 8c4 0 7 1 9 3M44 8c-3 2-5 4-6 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="20" cy="56" r="2" fill="currentColor" />
              </svg>
            </div>

            <div className={`absolute right-0 lg:right-[-4%] top-0 w-[72%] sm:w-[68%] lg:w-[70%] aspect-[5/7] rounded-none overflow-hidden shadow-xl transition-all duration-1000 ${imagesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/modern_construction_site_blue_hour.png" alt="Modern construction site during blue hour with architectural lighting" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-multiply" style={{
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n3'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n3)'/%3E%3C/svg%3E\")"
            }} aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}